import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Plus, X, Phone, MessageSquare, Trash2, Bus, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PageTitle, PageSubtitle, Loader, EmptyState, Card, BackHeader } from "@/components/common";
import { cn } from "@/lib/utils";

export default function CarriersPage() {
  const navigate = useNavigate();
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [chatLoading, setChatLoading] = useState("");

  const load = () => {
    setLoading(true);
    apiFetch("/carriers").then(setCarriers).catch((e) => setError(e.message || "Errore di caricamento.")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim() || !email.trim()) { setError("Inserisci ragione sociale ed email del vettore."); return; }
    setSaving(true);
    setError("");
    try {
      const c = await apiFetch("/carriers", { method: "POST", body: JSON.stringify({ name: name.trim(), email: email.trim() }) });
      setCarriers((old) => [c, ...old]);
      setShowForm(false);
      setName("");
      setEmail("");
    } catch (e) {
      setError(e.message || "Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const openChat = async (c) => {
    if (!c.carrier_user_id) return;
    setChatLoading(c.id);
    try {
      const chat = await apiFetch("/chats/start", { method: "POST", body: JSON.stringify({ user_id: c.carrier_user_id }) });
      navigate(`/chat/${chat.id}`, { state: { title: chat.title } });
    } catch (e) {
      setError(e.message || "Impossibile aprire la chat.");
    } finally {
      setChatLoading("");
    }
  };

  const remove = async (id) => {
    try {
      await apiFetch(`/carriers/${id}`, { method: "DELETE" });
      setCarriers((old) => old.filter((c) => c.id !== id));
    } catch (e) {
      setError(e.message || "Riprova.");
    }
  };

  const inputCls = "min-h-[50px] w-full rounded-xl border border-border bg-card px-3 text-[15px] outline-none focus:border-primary";
  const label = "mt-2 block text-xs font-bold text-card-foreground";

  return (
    <div className="space-y-3">
      <BackHeader />
      <div className="flex items-start justify-between">
        <div>
          <PageTitle>I miei vettori</PageTitle>
          <PageSubtitle>Quando pubblichi un viaggio puoi destinarlo solo ai vettori associati.</PageSubtitle>
        </div>
        <button data-testid="carrier-add-button" onClick={() => setShowForm((v) => !v)} className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
          {showForm ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
        </button>
      </div>

      {showForm ? (
        <Card>
          <p className="mb-1 text-lg font-extrabold text-foreground">Associa un vettore</p>
          <label className={label}>Ragione sociale</label>
          <input data-testid="carrier-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Trasporti Verdi" className={inputCls} />
          <label className={label}>Email del vettore</label>
          <input data-testid="carrier-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@vettore.it" className={inputCls} />
          <p className="mt-2 text-xs text-muted-foreground">Se l'email è registrata in CargoFlow come vettore, il collegamento è immediato.</p>
          <button data-testid="carrier-save-button" onClick={add} disabled={saving} className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} ASSOCIA VETTORE
          </button>
        </Card>
      ) : null}

      {error ? <p data-testid="carriers-error" className="text-center text-sm text-destructive">{error}</p> : null}

      {loading ? (
        <Loader />
      ) : carriers.length === 0 ? (
        <EmptyState icon={Users} title="Nessun vettore associato" text="Aggiungi i vettori con cui lavori per proporre loro viaggi riservati." />
      ) : (
        carriers.map((c) => (
          <Card key={c.id} data-testid={`carrier-card-${c.id}`} className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
              <Bus className="h-5 w-5 text-primary" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-extrabold text-foreground">{c.name}</p>
              <p className="truncate text-xs text-muted-foreground">{c.email}</p>
              <p className={cn("mt-1 text-[11px] font-bold", c.linked ? "text-[hsl(var(--success))]" : "text-muted-foreground")}>
                {c.linked ? "Collegato su CargoFlow" : "Non ancora registrato"}
              </p>
              {c.linked && c.rating_count ? (
                <p data-testid={`carrier-rating-${c.id}`} className="mt-0.5 text-xs font-extrabold text-[hsl(var(--warning))]">
                  ★ {c.rating_average} · {c.rating_count} {c.rating_count === 1 ? "recensione" : "recensioni"}
                </p>
              ) : null}
            </div>
            {c.linked && c.phone ? (
              <a data-testid={`carrier-call-${c.id}`} href={`tel:${c.phone.replace(/\s/g, "")}`} className="p-2 text-primary"><Phone className="h-5 w-5" /></a>
            ) : null}
            {c.linked && c.carrier_user_id ? (
              <button data-testid={`carrier-chat-${c.id}`} onClick={() => openChat(c)} className="p-2 text-primary">
                {chatLoading === c.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageSquare className="h-5 w-5" />}
              </button>
            ) : null}
            <button data-testid={`carrier-remove-${c.id}`} onClick={() => remove(c.id)} className="p-2 text-destructive"><Trash2 className="h-5 w-5" /></button>
          </Card>
        ))
      )}
    </div>
  );
}
